import { useMemo } from "react";
import { useSearchParams } from "react-router";

import {
  EmptystateModulesColored,
  EmptystateSearchNoResultsColored,
} from "components/icons/generated";
import SearchInput from "components/SearchInput";
import { getSearchQuery } from "components/SearchInput/helpers";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import { useActiveDrawerId } from "ds/components/DrawerNew/useActiveDrawerId";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import { useInitialSort } from "ds/components/Table/hooks/useInitialSort";
import TableSimple from "ds/components/TableSimple";
import useAnalytics from "hooks/useAnalytics";
import useTypedContext from "hooks/useTypedContext";
import { VersionState } from "types/generated";
import { fuzzySearch } from "utils/fuzzySearch";

import ModuleVersionPageIntoTitle from "../components/PageIntoTitle";
import ModuleVersionScrollableWrapper from "../components/ScrollableWrapper";
import { VersionContext } from "../Context";
import { FILTERS_ORDER_SETTINGS_KEY_VERSION_SUBMODULES, NO_STICKY_RIGHT } from "./constants";
import { MODULE_VERSION_SUBMODULES_TABLE_CELLS } from "./Table/Cells";
import {
  INITIAL_SORT_DIRECTION,
  INITIAL_SORT_OPTION,
  MODULE_VERSION_SUBMODULES_TABLE_FILTERS_STORAGE_KEY,
  SORT_CONFIG,
} from "./Table/constants";
import { useSortingModuleVersionSubmodules } from "./Table/useSortingModuleVersionSubmodules";
import useModuleVersionSubmodulesTableStore, {
  useModuleVersionSubmodulesNameOnlyTableStore,
} from "./Table/useTableStore";

const ModuleVersionSubmodules = () => {
  const { version } = useTypedContext(VersionContext);

  const [searchParams] = useSearchParams();
  const searchQuery = getSearchQuery(searchParams);
  const trackAnalytics = useAnalytics();
  const activeRowId = useActiveDrawerId();

  const canShowInstructions = !version.yanked && version.state === VersionState.Active;

  useInitialSort(INITIAL_SORT_OPTION, INITIAL_SORT_DIRECTION);

  const filteredSubmodules = useMemo(() => {
    const rows = (version.metadata?.submodules || []).map((submodule) => ({
      id: submodule.name,
      path: submodule.path,
      inputs: submodule.inputs,
    }));

    if (searchQuery) {
      return fuzzySearch(rows, searchQuery.trim(), {
        keys: ["id"],
        scoreThreshold: -1000,
      });
    }

    return rows;
  }, [version.metadata?.submodules, searchQuery]);

  const sortedSubmodules = useSortingModuleVersionSubmodules(filteredSubmodules);

  return (
    <>
      <PageInfoHeaderWrapper>
        <ModuleVersionPageIntoTitle
          title="Submodules"
          tooltip="Modules nested within this module that can be used independently."
        />
        <PageInfoHeaderActions>
          <SearchInput
            placeholder={SEARCH_PLACEHOLDERS.SUBMODULES}
            filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY_VERSION_SUBMODULES}
          />
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>
      <ModuleVersionScrollableWrapper padding="0">
        {sortedSubmodules.length === 0 && (
          <EmptyState
            padding="x-large"
            icon={searchQuery ? EmptystateSearchNoResultsColored : EmptystateModulesColored}
            caption={
              searchQuery
                ? "No submodules match your search query"
                : "This module has no submodules"
            }
            fullHeight
            announce={!!searchQuery}
          />
        )}
        {sortedSubmodules.length > 0 && (
          <TableSimple
            data={sortedSubmodules}
            store={
              canShowInstructions
                ? useModuleVersionSubmodulesTableStore
                : useModuleVersionSubmodulesNameOnlyTableStore
            }
            ariaLabel="Submodules"
            trackAnalytics={trackAnalytics}
            cellPerColumn={MODULE_VERSION_SUBMODULES_TABLE_CELLS}
            filtersStorageKey={MODULE_VERSION_SUBMODULES_TABLE_FILTERS_STORAGE_KEY}
            sortConfig={SORT_CONFIG}
            hasPagination={false}
            hideSettings
            stickyRight={NO_STICKY_RIGHT}
            activeRowId={activeRowId}
          />
        )}
      </ModuleVersionScrollableWrapper>
    </>
  );
};

export default ModuleVersionSubmodules;
